// src/Pricing.js
import React, { useEffect, useState } from "react";
import "./Sections.css";
import {
  fetchEntitlements,
  subscribe,
  openBillingPortal,
} from "./paymentsClient";
import { tUI } from "./i18n";

export function Pricing({ language, user, onRequireAuth }) {
  // renamed to avoid shadowing props
  const [entState, setEntState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadEntitlements() {
      try {
        const e = await fetchEntitlements();
        setEntState(e);
      } catch (err) {
        console.error("Failed to fetch entitlements", err);
      } finally {
        setLoading(false);
      }
    }
    loadEntitlements();
    if (window.location.search.includes("session_id")) loadEntitlements();
  }, []);

  const plan = (entState?.plan || "none").toUpperCase();
  const isActive = entState?.status === "active";

  const handleSubscribeClick = async (planKey) => {
    // 🔐 user signed out → open auth modal instead of Stripe
    if (!user) {
      if (onRequireAuth) onRequireAuth();
      return;
    }

    try {
      await subscribe(planKey);
    } catch (err) {
      alert("Could not start checkout. Please try again.");
    }
  };

  const handleManageClick = async () => {
    try {
      await openBillingPortal();
    } catch (err) {
      alert("Could not open billing portal. Please try again.");
    }
  };

  // Card data – same content as landing page pricing
  const cards = [
    {
      key: "standard",
      price: "€10.99",
      title: tUI(language, "card_ats_title"),
      subtitle: tUI(language, "card_ats_desc"),
      features: [
        tUI(language, "plan_ats_feature1"),
        tUI(language, "plan_ats_feature2"),
        tUI(language, "plan_ats_feature3"),
        tUI(language, "plan_ats_feature4"),
      ],
    },
    {
      key: "advanced",
      price: "€19.99",
      title: tUI(language, "card_ai_title"),
      subtitle: tUI(language, "card_ai_desc"),
      features: [
        tUI(language, "plan_ai_feature1"),
        tUI(language, "plan_ai_feature2"),
        tUI(language, "plan_ai_feature3"),
        tUI(language, "plan_ai_feature4"),
        tUI(language, "plan_ai_feature5"),
        tUI(language, "plan_ai_feature6"),
      ],
    },
    {
      key: "premium",
      price: "€8.99",
      title: tUI(language, "card_builder_title"),
      subtitle: tUI(language, "card_builder_desc"),
      features: [
        tUI(language, "plan_builder_feature1"),
        tUI(language, "plan_builder_feature2"),
        tUI(language, "plan_builder_feature3"),
        tUI(language, "plan_builder_feature4"),
        tUI(language, "plan_builder_feature5"),
        tUI(language, "plan_builder_feature6"),
        tUI(language, "plan_builder_feature7"),
      ],
    },
  ];
    // ✅ Show only Premium plan in Pricing page
  const cardsToShow = cards.filter((c) => c.key === "premium");

  return (
    <section className="bg-white min-h-screen">
      {loading ? (
        <div className="flex items-center justify-center pt-24">
          <p className="text-slate-600 text-sm">
            {tUI(language, "pricing_loading")}
          </p>
        </div>
      ) : (
        <div className="mx-auto max-w-[1000px] px-4 pt-24 pb-16">
          {/* Show current plan ONLY if user has an active plan */}
          {isActive && plan !== "NONE" && (
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-700">
                <strong>{tUI(language, "pricing_current_plan")}</strong>{" "}
                {plan} {tUI(language, "pricing_active")}
              </span>

              <button
                onClick={handleManageClick}
                className="rounded-full border border-[#06105933] px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {tUI(language, "pricing_manage")}
              </button>
            </div>
          )}

          {/* Pricing cards – same look as landing page */}
          <div className="grid grid-cols-1 place-items-center">
            <div className="w-full max-w-[360px]">
              {cardsToShow.map((card) => {
              const used = entState?.quota_used_month ?? 0;
              const total = entState?.quota_monthly ?? 0;
              const noCreditsLeft = total > 0 && used >= total;

              const isCurrent =
                entState?.plan &&
                entState.plan.toLowerCase() === card.key &&
                isActive &&
                !noCreditsLeft;

              return (
                <article
                  key={card.key}
                  className={
                    "h-full flex flex-col rounded-2xl border border-[#06105933] p-6 shadow-sm" +
                    (isCurrent ? " ring-2 ring-[#061059]" : "")
                  }
                >
                  {/* Title + subtitle */}
                  <h3 className="text-[16px] font-semibold whitespace-pre-line">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-[14px] text-slate-600">
                    {card.subtitle}
                  </p>

                  {/* Features */}
                  <ul className="mt-4 space-y-2 text-[13px] text-slate-600">
                    {card.features.map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                       <svg className="h-4 w-4" stroke="#061059" fill="none" strokeWidth="3" viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Price */}
                  <div className="mt-6">
                    <div className="text-[28px] font-bold">
                      {card.price}
                    </div>

                    <div className="mt-1 text-[13px] font-medium text-slate-500">
                      {tUI(language, "pricing_access_note")}
                    </div>
                  </div>

                  {/* Subscribe button */}
                  <button
                    onClick={() => handleSubscribeClick(card.key)}
                    disabled={isCurrent}
                    className="mt-5 w-full rounded-xl bg-[#061059] hover:bg-[#0A1575] px-4 py-2.5 text-[16px] font-semibold text-white ..."

                  >
                    {noCreditsLeft
                      ? tUI(language, "pricing_recharge")
                      : isCurrent
                      ? tUI(language, "pricing_current")
                      : tUI(language, "pricing_subscribe")}
                  </button>
                </article>
              );
            })}
          </div>
          </div>
          {/* Cancel anytime + Fair use + legal text */}
          {/* Cancel anytime + Fair use + legal text */}
          <div className="mt-10 max-w-[760px] mx-auto text-center text-[14px] text-slate-500 space-y-2">
            <p>{tUI(language, "pricing_secure_checkout")}</p>

            <p>
              <span className="font-semibold">
                {tUI(language, "pricing_fair_use_title")}
              </span>{" "}
              {tUI(language, "pricing_fair_use_body")}
            </p>

            <p>
              {tUI(language, "pricing_legal_intro")}{" "}
              <a
                href="/terms"
                className="underline hover:text-slate-700"
                target="_blank"
                rel="noreferrer"
              >
                {tUI(language, "pricing_legal_terms")}
              </a>{" "}
              {tUI(language, "pricing_legal_and")}{" "}
              <a
                href="/privacy"
                className="underline hover:text-slate-700"
                target="_blank"
                rel="noreferrer"
              >
                {tUI(language, "pricing_legal_privacy")}
              </a>
              .
            </p>
          </div>

        </div>
      )}
    </section>
  );
}
